import diffCellStyleRenderer from '@components/agile/agileComponents/agGridCommonComponents/DiffCellStyleRenderer';
import BuyableCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/BuyableCellRenderer';
import ProcurableCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/ProcurableCellRenderer';
import TemperatureRatingCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/TemperatureRatingCellRenderer';
import DeleteButtonCellRendererNew from '@components/agile/agileComponents/agGridCommonComponents/DeleteButtonCellRendererNew';
import ZoneSelectCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/ZoneSelectCellRenderer';
import SubCategoriesCellRendererNew from '@components/agile/agileComponents/agGridCommonComponents/SubCategoriesCellRendererNew';
import ImageCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/ImageCellRenderer';
import QuantityButtonCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/QuantityButtonCellRenderer';
import FohCapacityCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/FohCapacityCellRenderer';
import { AgileTableRow } from '@components/agile/types/AgileTableRow';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';

interface AgileTableColumnDefsProps {
  macroplanogramList: AgileMacroplanogram[];
  rowData: AgileTableRow[];
  setRowData: (data: AgileTableRow[]) => void;
  onZoneChange: (asin: string, oldZone: string, newZone: string, rowId?: string) => void;
  onDeleteRow: (asin: string, zoneName: string) => void;
  includeDraft?: boolean;
  onSubcategoryChange: (asin: string, oldCategory: string, newCategory: string) => void;
}

export const getAgileTableColumnDefs = ({
  macroplanogramList,
  rowData,
  setRowData,
  onZoneChange,
  onDeleteRow,
  includeDraft = false,
  onSubcategoryChange,
}: AgileTableColumnDefsProps) => [
  {
    headerName: 'Delete',
    field: 'Delete',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cellRenderer: (params: any) => {
      const isNotActionable = params.data?.recommendedAction === 'NOTACTIONABLE';
      if (isNotActionable) {
        return ''; // Don't show delete button for non-actionable items
      }
      return DeleteButtonCellRendererNew(params);
    },
    cellRendererParams: {
      onDelete: onDeleteRow,
    },
    sortable: false,
    filter: false,
    width: 80,
    pinned: true,
  },
  {
    headerName: 'Action',
    field: 'recommendedAction',
    width: 120,
    pinned: true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cellStyle: (params: any) => {
      const action = params.data?.recommendedAction;
      if (action === 'ADD') return { color: '#2e7d32', fontWeight: 'bold' };
      if (action === 'REMOVE') return { color: '#d32f2f', fontWeight: 'bold' };
      return { color: '#000000', fontWeight: 'normal' };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cellRenderer: (params: any) => {
      const action = params.data?.recommendedAction;
      return action ? action.toUpperCase() : '';
    },
  },
  {
    headerName: 'ASIN',
    field: 'asin',
    width: 120,
    pinned: true,
  },
  {
    headerName: 'Title',
    field: 'title',
    editable: false,
    width: 200,
    pinned: true,
  },
  {
    headerName: 'Image',
    field: 'image',
    editable: false,
    cellRenderer: ImageCellRenderer,
    filter: false,
    sortable: false,
    width: 80,
    maxWidth: 100,
    pinned: true,
  },
  {
    headerName: 'Zone',
    field: 'zoneName',
    editable: false,
    width: 200,
    cellRenderer: ZoneSelectCellRenderer,
    cellRendererParams: {
      macroplanogramList: macroplanogramList || [],
      onZoneChange: onZoneChange,
      includeDraft: includeDraft,
    },
  },
  {
    headerName: 'Subcategory',
    field: 'subcategoryId',
    cellRenderer: SubCategoriesCellRendererNew,
    cellRendererParams: {
      setRowData: setRowData,
      onSubcategoryChange: (asin: string, zoneName: string, newSubcategoryId: string) =>
        onSubcategoryChange(asin, zoneName, newSubcategoryId),
      rowData: rowData,
      setRecommendationFlag: () => {},
      listOfMacroplanogram: macroplanogramList,
      isNewAsinOnly: true,
    },
    width: 150,
  },
  {
    headerName: 'Zone Facings',
    field: 'zoneFacings',
    editable: false,
    width: 150,
  },
  {
    headerName: 'Remaining Zone Facings',
    field: 'remainingZoneFacings',
    cellRenderer: diffCellStyleRenderer,
    editable: false,
    filter: false,
  },
  {
    headerName: 'Facings',
    field: 'remainingFacings',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cellRenderer: (params: any) => {
      const isNotActionable = params.data?.recommendedAction === 'NOTACTIONABLE';
      if (isNotActionable) {
        return params.value; // Just show the number for non-actionable items
      }
      return QuantityButtonCellRenderer(params);
    },
    flex: 2,
    minWidth: 180,
    maxWidth: 180,
    filter: false,
  },
  {
    headerName: 'Diff',
    field: 'difference',
    cellRenderer: diffCellStyleRenderer,
    filter: false,
    width: 80,
  },
  {
    headerName: 'Total FOH Capacity (Units)',
    wrapHeaderText: true,
    headerClass: 'text-center',
    field: 'fohCapacity',
    cellRenderer: FohCapacityCellRenderer,
    cellRendererParams: {
      rowData,
      setRowData,
      setRecommendationFlag: () => {},
      zoneDetails: undefined,
    },
    editable: false,
    filter: false,
  },
  {
    headerName: 'Buyable',
    field: 'buyable',
    cellRenderer: BuyableCellRenderer,
    width: 120,
  },
  {
    headerName: 'Procurable',
    field: 'procurable',
    cellRenderer: ProcurableCellRenderer,
    width: 150,
  },
  {
    headerName: 'Temp Zone',
    field: 'temperatureRating',
    cellRenderer: TemperatureRatingCellRenderer,
  },
  {
    headerName: 'Shrink Cost (-1W)',
    field: 'shrinkCost',
    filter: true,
  },
  {
    headerName: 'Shrink % Ops (-1W) %',
    field: 'shrinkPerOps',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Shrink % Ops (-13W) %',
    field: 'shrinkPerOps13week',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Shrink % Ops (-26W) %',
    field: 'shrinkPerOps26week',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Shrink % Ops (-52W) %',
    field: 'shrinkPerOps52week',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Moving Avg Sales (-13W) $',
    field: 'movingAvgSales13week',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Moving Avg Sales (-26W) $',
    field: 'movingAvgSales26week',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Moving Avg Sales (-52W) $',
    field: 'movingAvgSales52week',
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
];
